import {
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { JoDetail, JoHeader } from "app/model/entities/entity-model";
import { AddonBinStatusEnum } from "app/model/entities/enums";
import { DomainBaseComponent } from "app/shared/domain-base.component";
import { DomainService } from "app/shared/domain.service";
import { PrintFns } from "app/shared/print-fns";
import { DateFns } from "app/shared/date-fns";
import { UtilFns } from "app/shared/util-fns";
import { environment } from "environments/environment";
import _ from "lodash";
import { takeUntil } from "rxjs/operators";

interface ReportRow {
  manufacturer: string;
  sku: string;
  description: string;
  features: string;
  orderedQty: number;
  cancelledQty: number;
  shippedQty: number;
  openQty: number;
  expectedShipDate: string;
  binNumber: string;
}

interface JoGroup {
  joHeader: JoHeader;
  rows: ReportRow[];
}

interface ShippingAddressGroup {
  shippingName: string;
  shippingAddress1: string;
  shippingAddress2: string;
  shippingAddress3: string;
  shippingCity: string;
  shippingState: string;
  shippingZipCode: string;
  joGroups: JoGroup[];
}

@Component({
  selector: "app-printable-open-jo-detail",
  templateUrl: "./printable-open-jo-detail.component.html",
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class PrintableOpenJoDetailComponent
  extends DomainBaseComponent
  implements OnDestroy
{
  @ViewChild("printableArea") printElementRef: ElementRef;

  shippingAddressGroups: ShippingAddressGroup[] = [];
  accountName = "All Accounts";
  timestamp: string;
  pdfurl: string;

  private accountId: number | null = null;
  private shippingAddress1: string | null = null;

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,
    private elementRef: ElementRef,
  ) {
    super(domainService);
    this.route.queryParamMap
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.updateFromContext();
      });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  async updateFromContext() {
    await this.dbQueryService.cacheAll();

    const acctParam = this.route.snapshot.queryParams["accountId"];
    this.accountId = acctParam ? parseInt(acctParam, 10) : null;

    // Read shippingAddress1 filter from URL (passed by widget)
    this.shippingAddress1 = this.route.snapshot.queryParams["shipAddr1"] || null;

    // Query with server-side filtering by account + shippingAddress1
    const joDetails = await this.dbQueryService.getOpenJoDetailReport(
      this.accountId, this.shippingAddress1
    );

    if (this.accountId && joDetails.length > 0) {
      this.accountName = joDetails[0].joHeader.account.accountName;
    }

    this.shippingAddressGroups = this.buildShippingAddressGroups(joDetails);
    this.timestamp = DateFns.fmtDateTimeTimeZone(new Date());
    this.buildPdfUrl();

    this.isPageReady = true;

    setTimeout(() => {
      const mediaQueryList = window.matchMedia("print");
      if (
        mediaQueryList.matches ||
        window.navigator.userAgent.includes("Headless")
      ) {
        this.preparePrint();
      }
    }, 0);
  }

  private buildShippingAddressGroups(joDetails: JoDetail[]): ShippingAddressGroup[] {
    const byShippingKey = _.groupBy(joDetails, (jod) => jod.joHeader.shippingKey);
    const groups: ShippingAddressGroup[] = [];

    for (const [, jods] of Object.entries(byShippingKey)) {
      const firstJoh = jods[0].joHeader;

      const byJoHeader = _.groupBy(jods, (jod) => jod.joHeaderId);
      const joGroups: JoGroup[] = [];

      for (const headerJods of Object.values(byJoHeader)) {
        const joHeader = headerJods[0].joHeader;
        const rows = headerJods.map((jod) => this.buildRow(jod));
        joGroups.push({ joHeader, rows });
      }

      joGroups.sort((a, b) => a.joHeader.id - b.joHeader.id);

      groups.push({
        shippingName: firstJoh.shippingName || "",
        shippingAddress1: firstJoh.shippingAddress1 || "",
        shippingAddress2: firstJoh.shippingAddress2 || "",
        shippingAddress3: firstJoh.shippingAddress3 || "",
        shippingCity: firstJoh.shippingCity || "",
        shippingState: firstJoh.shippingState || "",
        shippingZipCode: firstJoh.shippingZipCode || "",
        joGroups,
      });
    }

    groups.sort((a, b) => a.shippingName.localeCompare(b.shippingName));
    return groups;
  }

  private buildRow(jod: JoDetail): ReportRow {
    const pt = jod.product.productType;
    const mfr = pt.manufacturer;

    const features = jod.product.getFeatureChoicesExtract();
    const shippedQty = jod.getItemDetailsShippedQty();

    // Get process bin info from open item details via ItemDetailAddons → AddonBin
    const openItemDetails = jod.itemDetails.filter(
      (itd) => itd.itemDetailStatusId < 50
    );
    const binDescriptions = new Map<number, string>();
    for (const itd of openItemDetails) {
      for (const itda of (itd.itemDetailAddons || [])) {
        const ab = itda.addonBin;
        if (ab && ab.addonBinStatusId !== AddonBinStatusEnum.Closed && !binDescriptions.has(ab.id)) {
          const stationName = ab.currentAddonStation?.name || ab.pendingAddonStation?.name || "";
          const stationLabel = ab.currentAddonStation ? "" : (ab.pendingAddonStation ? "Pending " : "");
          binDescriptions.set(ab.id, stationName ? `${ab.id} ${stationLabel}${stationName}` : `${ab.id}`);
        }
      }
    }
    const binNumbers = Array.from(binDescriptions.values()).sort();
    const openQty = jod.orderQty - jod.cancelQty - shippedQty;

    return {
      manufacturer: mfr.name,
      sku: pt.style || "",
      description: pt.description || "",
      features,
      orderedQty: jod.orderQty,
      cancelledQty: jod.cancelQty,
      shippedQty,
      openQty,
      expectedShipDate: jod.calcExpectedShipDate ? DateFns.fmtDate(jod.calcExpectedShipDate) : "",
      binNumber: binNumbers.length > 0 ? binNumbers.join(", ") : (openQty > 0 ? (openItemDetails.length === 0 ? "On Order" : "ShipPrep") : ""),
    };
  }

  private buildPdfUrl() {
    let routePath = "printable-open-jo-detail";
    const params: string[] = [];
    if (this.accountId) {
      params.push("accountId=" + this.accountId);
    }
    if (this.shippingAddress1) {
      params.push("shipAddr1=" + encodeURIComponent(this.shippingAddress1));
    }
    if (params.length > 0) {
      routePath += "?" + params.join("&");
    }
    this.pdfurl =
      environment.apiRoot +
      "api/pdf/pdffromurl/OpenJoDetailReport" +
      "?url=" +
      encodeURIComponent(UtilFns.getBaseUrl() + routePath) +
      "&headerLeft=" +
      encodeURIComponent("Open Job Order Detail");
  }

  preparePrint() {
    let el: HTMLElement =
      this.printElementRef && this.printElementRef.nativeElement;
    if (el == null) {
      el = this.elementRef && this.elementRef.nativeElement;
    }
    if (!el) {
      return;
    }
    PrintFns.printElement(el);
  }

  printDocument() {
    window.open(this.pdfurl, "_blank");
  }
}
